import Tracker, { type Options as TrackerOptions } from '@openreplay/tracker';
import trackerAssist from '@openreplay/tracker-assist';
import { createGraphqlMiddleware } from '@openreplay/tracker-graphql';
import trackerZustand from '@openreplay/tracker-zustand';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
} from 'react';
import type { StateCreator, StoreMutatorIdentifier } from 'zustand';

import { useGetLoggedInUser } from '@revelio/auth';
import { E2E_TEST_USER_SHARED_NAME } from '@revelio/core/e2e-testing-users';

import { environment } from '../../environments/environment';
import { previewJson } from './preview-json';

const isLocalHost = window.location.hostname === 'localhost';
const isProd = environment.production;

type NetworkOptions = NonNullable<TrackerOptions['network']>;
type NetworkSanitizer = NonNullable<NetworkOptions['sanitizer']>;
type RequestResponseData = Parameters<NetworkSanitizer>[0];

const getPathname = (url: string | undefined): string => {
  try {
    return new URL(url ?? '', window.location.href).pathname;
  } catch {
    return '';
  }
};

const trackerOptions: Partial<TrackerOptions> = {
  __DISABLE_SECURE_MODE: isLocalHost, // for local use only
  projectKey: environment.OPEN_REPLAY_PROJECT_KEY, // this key is an empty string for all environments besides local dev and prod
  crossdomain: {
    enabled: true,
  },
  captureIFrames: true,
  defaultInputMode: 0,
  obscureInputDates: false,
  obscureInputNumbers: false,
  obscureInputEmails: false,
  obscureTextNumbers: false,
  obscureTextEmails: false,
  captureExceptions: true,
  capturePerformance: true,
  capturePageLoadTimings: true,
  capturePageRenderTimings: true,
  network: {
    useProxy: !isLocalHost,
    sessionTokenHeader: false,
    failuresOnly: false,
    ignoreHeaders: false,
    capturePayload: true,
    captureInIframes: true,
    sanitizer: (data: RequestResponseData) => {
      // if download request, remove response body
      if (getPathname(data.url).includes('/download')) {
        data.response.body = null;
      }

      // Truncate large response bodies
      if (data.response.body) {
        data.response.body = previewJson(data.response.body);
      }

      return data;
    },
  },
};

// Constructing the tracker patches DOM prototypes (e.g. `adoptedStyleSheets`),
// which throws when a browser extension has already redefined one of them as
// non-configurable. This module is evaluated during app boot, so run without
// session replay rather than let that blank the whole dashboard.
const createTracker = (): Tracker | null => {
  try {
    return new Tracker(trackerOptions);
  } catch (error) {
    console.error(
      'OpenReplay failed to initialise; session replay is disabled for this page load.',
      error
    );
    return null;
  }
};

const tracker = createTracker();

const recordGraphQL = tracker?.use(createGraphqlMiddleware());

// Zustand middleware: store mutations become part of the session replay.
// Inert until tracker.start() (prod, non-test users only).
// tracker-zustand's own types bind to its bundled zustand@4, so rebind the
// logger against the app's zustand@5 StateCreator/mutator generics here.
type ZustandStateLogger = <
  T,
  Mps extends [StoreMutatorIdentifier, unknown][] = [],
  Mcs extends [StoreMutatorIdentifier, unknown][] = [],
>(
  f: StateCreator<T, Mps, Mcs>,
  name?: string
) => StateCreator<T, Mps, Mcs>;

// `tracker` is null when construction failed — fall back to an identity
// middleware so store creation never depends on tracking being available.
const zustandLogger: unknown = tracker?.use(trackerZustand());
export const openReplayStateLogger: ZustandStateLogger = (
  typeof zustandLogger === 'function' ? zustandLogger : <T,>(f: T) => f
) as ZustandStateLogger;

// Session replay deep link for the current moment — attached to error logs so
// Slack alerts can jump straight into the replay. Undefined until a session
// is being recorded.
export const getOpenReplaySessionUrl = (): string | undefined =>
  tracker?.getSessionURL({ withCurrentTime: true });

// Makes failing operations searchable as events in OpenReplay (the GraphQL
// devtools tab already shows them, but events are filterable in search).
export const recordGqlErrorEvent = (operationName: string, message: string) => {
  try {
    tracker?.event('gql_error', { operation: operationName, message });
  } catch {
    // tracking must never break error logging
  }
};

export const recordTruncatedGraphQL = (
  kind: string,
  operationName: string,
  variables: unknown,
  data: unknown,
  duration: number
) => {
  const truncatedData = previewJson(data);
  return recordGraphQL?.(
    kind,
    operationName,
    variables,
    truncatedData,
    duration
  );
};

interface OpenReplayState {
  tracker: Tracker | null;
  recordGraphQL: typeof recordGraphQL;
}

const OpenReplayContext = createContext<OpenReplayState>({
  tracker,
  recordGraphQL,
});

export const OpenReplayProvider = ({ children }: PropsWithChildren) => {
  const { loggedInUser } = useGetLoggedInUser();

  useEffect(() => {
    const isTestUser = loggedInUser?.email?.includes(E2E_TEST_USER_SHARED_NAME);

    // checking we have user email so that we don't start tracking until we're sure it's not a test user session
    if (tracker && isProd && loggedInUser?.email && !isTestUser) {
      tracker.use(trackerAssist());
      tracker.start();
    }

    return () => {
      if (isProd) tracker?.stop();
    };
  }, [loggedInUser?.email]);

  return (
    <OpenReplayContext.Provider value={{ tracker, recordGraphQL }}>
      {children}
    </OpenReplayContext.Provider>
  );
};

export const useOpenReplay = () => useContext(OpenReplayContext);
